import {assertPartialSchema, createAction, useSelector, v} from '../../../lib'
import {TimeSeriesDataSource} from '../../../model/chart'
import {DexcomEventData} from '../../../shared/mongo/localdate_dexcom_data'
import {GarminDeviceLogDataType} from '../../../shared/mongo'
import {DB_NAME, DbOperationResult} from '../../db'
import {VisualizerGraphDataType} from '../../../shared/db'
import {ChunkMetaData, MetaDataMap} from '../../../shared/mongo/localdate_device_logdata_metadata'

export enum ParticipantTimeSeriesDataActionType {
  PARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET = 'PARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET',
  PARTICIPANT_GARMIN_TIMESERIES_DATA_SET = 'PARTICIPANT_GARMIN_TIMESERIES_DATA_SET',
  PARTICIPANT_DEXCOM_DATA_SET = 'PARTICIPANT_DEXCOM_DATA_SET',
  PARTICIPANT_ANALYSIS_DATA_SET = 'PARTICIPANT_ANALYSIS_DATA_SET',
  PARTICIPANT_ANALYSIS_DATA_SET_EMPTY = 'PARTICIPANT_ANALYSIS_DATA_SET_EMPTY',
  PARTICIPANT_TIMESERIES_DATA_DELETE = 'PARTICIPANT_GARMIN_DIRECT_DATA_DELETE',
}
export const doPARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET = createAction(
  ParticipantTimeSeriesDataActionType.PARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET,
)
export const doPARTICIPANT_GARMIN_TIMESERIES_DATA_SET = createAction(
  ParticipantTimeSeriesDataActionType.PARTICIPANT_GARMIN_TIMESERIES_DATA_SET,
)
export const doPARTICIPANT_ANALYSIS_DATA_SET = createAction(
  ParticipantTimeSeriesDataActionType.PARTICIPANT_ANALYSIS_DATA_SET,
)
export const doPARTICIPANT_ANALYSIS_DATA_SET_EMPTY = createAction(
  ParticipantTimeSeriesDataActionType.PARTICIPANT_ANALYSIS_DATA_SET_EMPTY,
)
export const doPARTICIPANT_DEXCOM_DATA_SET = createAction(
  ParticipantTimeSeriesDataActionType.PARTICIPANT_DEXCOM_DATA_SET,
)
export const doPARTICIPANT_TIMESERIES_DATA_DELETE = createAction(
  ParticipantTimeSeriesDataActionType.PARTICIPANT_TIMESERIES_DATA_DELETE,
)

export interface TimeSeriesDataState {
  [participantId: string]: ParticipantTimeSeriesDataState
}

export interface ParticipantTimeSeriesDataState {
  digestCoveredIndexes?: number[]
  digest?: Partial<Record<GarminDeviceLogDataType, number[]>>
  data?: {
    [dataType in VisualizerGraphDataType]?: Record<string, boolean>
  }
}

export interface TimeSeriesData {
  timeSeriesRaw: TimeSeriesDataSource[]
  timeSeriesForPlot: TimeSeriesDataSource[]
  timezoneRef: string
  timeOffsetRef: number
}

export interface TransformedGarminDeviceLogData extends TimeSeriesData {
  metaDataMap: MetaDataMap<ChunkMetaData>
  startChunkUtcIndex: number
}

export interface GlucoseTimeSeriesData {
  rawData: TimeSeriesDataSource[]
  timeSeriesForPlot: TimeSeriesDataSource[]
  events?: DexcomEventData[]
}

interface RootState {
  participantTimeSeriesData: TimeSeriesDataState
}
/* selector */
export const selectParticipantTimeSeriesData = (participantId: string) => {
  return useSelector((state: RootState) => state.participantTimeSeriesData[participantId])
}

export const selectParticipantTimeSeriesDataByDataType = (participantId: string, dataType: VisualizerGraphDataType) => {
  return useSelector((state: RootState) => state.participantTimeSeriesData[participantId]?.data?.[dataType])
}

export const participantTimeSeriesDataActionCreators = {
  doPARTICIPANT_GARMIN_TIMESERIES_DATA_SET,
  doPARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET,
  doPARTICIPANT_ANALYSIS_DATA_SET,
  doPARTICIPANT_ANALYSIS_DATA_SET_EMPTY,
  doPARTICIPANT_DEXCOM_DATA_SET,
  doPARTICIPANT_TIMESERIES_DATA_DELETE,
}

export const participantTimeSeriesDataDefaultState: TimeSeriesDataState = {}

type Action =
  | {
      type: ParticipantTimeSeriesDataActionType.PARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET
      payload: {
        projectDataDigest: {
          projectId: string
          coveredYYMMIndex: number[]
          participantDataDigestMap: {
            [participantId: string]: {
              garminDeviceDataDatesByType?: Record<GarminDeviceLogDataType, number[]>
            }
          }
        }
      }
    }
  | {
      type: ParticipantTimeSeriesDataActionType.PARTICIPANT_GARMIN_TIMESERIES_DATA_SET
      payload: {
        participantId: string
        yymmddIndex: number
        dbOperationResultList: DbOperationResult<VisualizerGraphDataType>[]
      }
    }
  | {
      type: ParticipantTimeSeriesDataActionType.PARTICIPANT_ANALYSIS_DATA_SET
      payload: {
        dbOperationResultList: DbOperationResult<VisualizerGraphDataType>[]
      }
    }
  | {
      type: ParticipantTimeSeriesDataActionType.PARTICIPANT_ANALYSIS_DATA_SET_EMPTY
      payload: {
        participantId: string
        yymmddIndex: number
        dataType: VisualizerGraphDataType
      }
    }
  | {
      type: ParticipantTimeSeriesDataActionType.PARTICIPANT_DEXCOM_DATA_SET
      payload: {
        participantId: string
        yymmddIndexes: number[]
        dbOperationResultList: DbOperationResult<VisualizerGraphDataType>[]
      }
    }
  | {
      type: ParticipantTimeSeriesDataActionType.PARTICIPANT_TIMESERIES_DATA_DELETE
      payload: {
        participantIdList: string[]
      }
    }

export const participantTimeSeriesDataReducer = (
  state: TimeSeriesDataState = participantTimeSeriesDataDefaultState,
  {type, payload}: Action,
) => {
  switch (type) {
    case ParticipantTimeSeriesDataActionType.PARTICIPANT_GARMIN_DIRECT_DATA_DIGEST_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          projectDataDigest: v.object({
            projectId: v.string().exist(),
            coveredYYMMIndex: v.array().items(v.number()).exist(),
            participantDataDigestMap: v.object().exist(),
          }),
        }),
      })

      let nextState = state

      for (const [participantId, digestData] of Object.entries(payload.projectDataDigest.participantDataDigestMap)) {
        const existingParticipantData = state[participantId]

        if (nextState === state) {
          nextState = {...state}
        }

        if (!existingParticipantData) {
          nextState[participantId] = {
            digestCoveredIndexes: [...payload.projectDataDigest.coveredYYMMIndex],
            digest: digestData.garminDeviceDataDatesByType ? {...digestData.garminDeviceDataDatesByType} : undefined,
          }
          continue
        }

        const nextDigest = digestData.garminDeviceDataDatesByType
          ? {...existingParticipantData.digest}
          : existingParticipantData.digest

        if (digestData.garminDeviceDataDatesByType) {
          for (const [dataType, dataDates] of Object.entries(digestData.garminDeviceDataDatesByType)) {
            const dataTypeEnum = Object.values(GarminDeviceLogDataType).find((value) => value === dataType)

            if (dataTypeEnum && nextDigest) {
              nextDigest[dataTypeEnum] = Array.from(new Set([...(nextDigest[dataTypeEnum] ?? []), ...dataDates]))
            }
          }
        }

        nextState[participantId] = {
          ...existingParticipantData,
          digestCoveredIndexes: Array.from(
            new Set([
              ...(existingParticipantData.digestCoveredIndexes ?? []),
              ...payload.projectDataDigest.coveredYYMMIndex,
            ]),
          ),
          digest: nextDigest,
        }
      }

      return nextState
    }

    case ParticipantTimeSeriesDataActionType.PARTICIPANT_GARMIN_TIMESERIES_DATA_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          participantId: v.string().uuid().required(),
          yymmddIndex: v.number().required(),
          dbOperationResultList: v.array().items(
            v.object({
              dbName: v.string().required(),
              participantId: v.string().uuid().required(),
              dataType: v.string().required(),
              yymmddIndex: v.number().required(),
              operation: v.string().required(),
              result: v.bool().required(),
            }),
          ),
        }),
      })

      return updateTimeSeriesDataState(state, payload.dbOperationResultList)
    }

    case ParticipantTimeSeriesDataActionType.PARTICIPANT_ANALYSIS_DATA_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          dbOperationResultList: v.array().items(
            v.object({
              dbName: v.string().required(),
              participantId: v.string().uuid().required(),
              dataType: v.string().required(),
              yymmddIndex: v.number().required(),
              operation: v.string().required(),
              result: v.bool().required(),
            }),
          ),
        }),
      })
      return updateTimeSeriesDataState(state, payload.dbOperationResultList)
    }

    case ParticipantTimeSeriesDataActionType.PARTICIPANT_ANALYSIS_DATA_SET_EMPTY: {
      assertPartialSchema({
        payload,
        schema: v.object({
          data: v.array().items(
            v.object({
              participantId: v.string().uuid().required(),
              dataType: v.string().required(),
            }),
          ),
        }),
      })
      const {participantId, yymmddIndex, dataType} = payload
      return updateEmptyTimeSeriesData(state, participantId, dataType, [yymmddIndex])
    }

    case ParticipantTimeSeriesDataActionType.PARTICIPANT_DEXCOM_DATA_SET: {
      assertPartialSchema({
        payload,
        schema: v.object({
          participantId: v.string().uuid().required(),
          yymmddIndexes: v.array().items(v.number()).required(),
          dbOperationResultList: v.array().items(
            v.object({
              dbName: v.string().required(),
              participantId: v.string().uuid().required(),
              dataType: v.string().required(),
              yymmddIndex: v.number().required(),
              operation: v.string().required(),
              result: v.bool().required(),
            }),
          ),
        }),
      })
      if (payload.dbOperationResultList.length > 0) {
        return updateTimeSeriesDataState(state, payload.dbOperationResultList)
      } else {
        const {participantId, yymmddIndexes} = payload
        return updateEmptyTimeSeriesData(
          state,
          participantId,
          VisualizerGraphDataType.DexcomBloodGlucose,
          yymmddIndexes,
        )
      }
    }

    case ParticipantTimeSeriesDataActionType.PARTICIPANT_TIMESERIES_DATA_DELETE: {
      assertPartialSchema({
        payload,
        schema: v.object({
          participantIdList: v.array().items(v.string()),
        }),
      })
      const nextState = {...state}
      for (const participantId of payload.participantIdList) {
        delete nextState[participantId]
      }

      return nextState
    }
    default:
      return state
  }
}

function updateTimeSeriesDataState(
  state: TimeSeriesDataState,
  dbOperationResultList: DbOperationResult<VisualizerGraphDataType>[],
): TimeSeriesDataState {
  const newParticipantDataMap: {[participantId: string]: ParticipantTimeSeriesDataState} = {}

  dbOperationResultList.forEach((dbResult) => {
    const {dbName, participantId, dataType, yymmddIndex, result} = dbResult

    if (dbName == DB_NAME.TimeSeriesForChart) {
      if (!newParticipantDataMap[participantId]) {
        newParticipantDataMap[participantId] = {}
      }
      if (!newParticipantDataMap[participantId].data) {
        newParticipantDataMap[participantId].data = {}
      }
      const participantData = newParticipantDataMap[participantId].data
      if (participantData) {
        if (dataType) {
          if (!participantData[dataType]) {
            participantData[dataType] = {}
          }
          const typeData = participantData[dataType]
          if (typeData) {
            typeData[yymmddIndex] = result
          }
        }
      }
    }
  })

  let nextState = state

  for (const [participantId, participantData] of Object.entries(newParticipantDataMap)) {
    const existingParticipantData = state[participantId]

    if (nextState === state) {
      nextState = {...state}
    }

    if (!existingParticipantData) {
      nextState[participantId] = participantData
      continue
    }

    if (!participantData.data) {
      nextState[participantId] = {...existingParticipantData}
      continue
    }

    if (!existingParticipantData.data) {
      nextState[participantId] = {
        ...existingParticipantData,
        data: participantData.data,
      }
      continue
    }

    const nextParticipantData = {...existingParticipantData.data}

    for (const [dataType, typeData] of Object.entries(participantData.data)) {
      const graphDataType = dataType as VisualizerGraphDataType
      nextParticipantData[graphDataType] = {
        ...nextParticipantData[graphDataType],
        ...typeData,
      }
    }

    nextState[participantId] = {
      ...existingParticipantData,
      data: nextParticipantData,
    }
  }

  return nextState
}

function updateEmptyTimeSeriesData(
  state: TimeSeriesDataState,
  participantId: string,
  dataType: VisualizerGraphDataType,
  yymmddIndexes: number[],
): TimeSeriesDataState {
  const existingParticipantData = state[participantId] ?? {}
  const existingData = existingParticipantData.data ?? {}
  const nextTypeData = {...(existingData[dataType] ?? {})}

  for (const yymmddIndex of yymmddIndexes) {
    nextTypeData[`${yymmddIndex}`] = false
  }

  return {
    ...state,
    [participantId]: {
      ...existingParticipantData,
      data: {
        ...existingData,
        [dataType]: nextTypeData,
      },
    },
  }
}
